/** Max automatic recovery attempts for a valid live stream after playback errors. */
export const MAX_LIVE_RETRY_ATTEMPTS = 3;

/** Delay between live retries (3s → attempts at 3s / 6s / 9s from first failure). */
export const LIVE_RETRY_DELAY_MS = 3000;

export type LiveRetryScheduler = {
  /** @returns true when a retry was scheduled (error UI should stay hidden). */
  schedule: (message: string) => boolean;
  /** Clears pending timer + attempt count (e.g. on success or media change). */
  reset: () => void;
  /** Clears only the pending timer without resetting the attempt count. */
  clearTimer: () => void;
};

type CreateLiveRetrySchedulerParams = {
  /** Recreate player session / reload content. */
  onRetry: () => void;
  /** Show the final error UI after all attempts fail. */
  showPlaybackError: (message: string) => void;
  /** Toggle the single "Connecting to server..." label. */
  setReconnecting: (value: boolean) => void;
};

/**
 * Live-only playback recovery: up to 3 retries at 3s intervals.
 * Non-live / invalid callers should skip this and call `showPlaybackError` directly.
 */
export function createLiveRetryScheduler({
  onRetry,
  showPlaybackError,
  setReconnecting,
}: CreateLiveRetrySchedulerParams): LiveRetryScheduler {
  let attempt = 0;
  let timer: ReturnType<typeof setTimeout> | null = null;

  const clearTimer = () => {
    if (timer == null) return;
    clearTimeout(timer);
    timer = null;
  };

  const removeConnectingLabel = () => {
    try {
      setReconnecting(false);
    } catch {
      /* label setter may be gone during unmount */
    }
  };

  const reset = () => {
    clearTimer();
    attempt = 0;
    removeConnectingLabel();
  };

  const schedule = (message: string): boolean => {
    // Prevent overlapping timers / duplicate labels
    if (timer != null) {
      return true;
    }

    if (attempt >= MAX_LIVE_RETRY_ATTEMPTS) {
      clearTimer();
      attempt = 0;
      removeConnectingLabel();
      showPlaybackError(message);
      return false;
    }

    attempt += 1;

    try {
      setReconnecting(true);
    } catch {
      /* ignore missing label target */
    }

    timer = setTimeout(() => {
      timer = null;
      try {
        onRetry();
      } catch {
        /* recreate may fail if player already torn down */
      }
    }, LIVE_RETRY_DELAY_MS);

    return true;
  };

  return { schedule, reset, clearTimer };
}
